import { FC, useEffect, useMemo, useState } from 'react'
import {
  Text,
  InputSelectControl,
  Primary13Regular,
  TextFieldInput,
  GreyGrey60,
  Button,
  Tooltip,
  Flex
} from '@sequencehq/core-components'
import { formatAddress } from '@sequencehq/utils'
import { CountriesAlpha2 } from '@sequencehq/api'
import { getStateOptions } from '@sequencehq/api/utils/commonEnums'
import { Address } from '@sequencehq/api/utils/commonModels'
import { NO_ADDRESS_LABEL } from 'modules/Customers/data'
import { AggregatedInvoice } from 'Invoices/types'
import { countryOptions } from 'Invoices/utils'
import { usePaymentProviderForInvoice } from 'Invoices/hooks/usePaymentProviderForInvoice'
import { useSetAtom } from 'jotai'
import { editingRowIdAtom, editingRowLockedAtom } from './atoms'

type DraftAddress = Omit<Address, 'country'> & {
  country: CountriesAlpha2 | ''
}

type Props = {
  isEditing: boolean
  clientInvoice: AggregatedInvoice
  partialUpdateInvoice: (update: Partial<AggregatedInvoice>) => void
}

export const SentInvoiceBillingAddress: FC<Props> = ({
  isEditing,
  clientInvoice,
  partialUpdateInvoice
}) => {
  const address = clientInvoice.customerBillingAddress
  const customerName = clientInvoice.customerLegalCompanyName

  if (isEditing) {
    return (
      <EditMode
        address={address}
        customerName={customerName}
        onSave={updatedAddress =>
          partialUpdateInvoice({ customerBillingAddress: updatedAddress })
        }
      />
    )
  }

  return <ViewMode address={address} customerName={customerName} />
}

const ViewMode: FC<{ address: Address | null; customerName: string }> = ({
  address,
  customerName
}) => {
  const lines = formatAddress(address)

  return (
    <Flex flexDir="column" gap="2px">
      {lines.length > 0 ? (
        lines.map((line, i) => (
          <Text key={i} {...Primary13Regular}>
            {line}
          </Text>
        ))
      ) : (
        <Text {...Primary13Regular} color={GreyGrey60}>
          {NO_ADDRESS_LABEL}
        </Text>
      )}
    </Flex>
  )
}

const EditMode: FC<{
  address: Address | null
  customerName: string
  onSave: (address: Address) => void
}> = ({ address, customerName, onSave }) => {
  const setEditingRowId = useSetAtom(editingRowIdAtom)
  const setEditingRowLocked = useSetAtom(editingRowLockedAtom)
  const paymentProvider = usePaymentProviderForInvoice()
  const isGoCardlessEnabled = paymentProvider === 'GOCARDLESS'
  // GoCardless locks the country because a mandate is bound to it — but only
  // once there is one. With no address yet there is nothing to protect, and
  // leaving it disabled would make the row unsavable: no country means no
  // address to save.
  const isCountryLocked = isGoCardlessEnabled && Boolean(address?.country)
  const [localAddress, setLocalAddress] = useState<DraftAddress>(
    address ?? { line1: '', town: '', postcode: '', country: '' }
  )
  const savableAddress = toSavableAddress(localAddress)

  useEffect(() => {
    setEditingRowLocked(true)
    return () => setEditingRowLocked(false)
  }, [setEditingRowLocked])

  const stateOptions = useMemo(
    () => (localAddress.country ? getStateOptions(localAddress.country) : []),
    [localAddress.country]
  )

  const updateField = <K extends keyof DraftAddress>(
    field: K,
    value: DraftAddress[K]
  ) => {
    setLocalAddress(prev => ({ ...prev, [field]: value }))
  }

  const handleCountryChange = (newCountry: string) => {
    const newStateOptions = getStateOptions(newCountry as CountriesAlpha2)
    setLocalAddress(prev => ({
      ...prev,
      country: newCountry as CountriesAlpha2,
      ...(newStateOptions.length === 0 ? { state: undefined } : {})
    }))
  }

  const handleSave = () => {
    if (!savableAddress) return
    onSave(savableAddress)
    setEditingRowId(null)
  }

  const handleCancel = () => {
    setEditingRowId(null)
  }

  return (
    <Flex
      flexDir="column"
      gap="4px"
      padding="4px 0"
      onClick={e => e.stopPropagation()}
    >
      <TextFieldInput
        value={localAddress.line1}
        onChange={e => updateField('line1', e.target.value)}
        placeholder="Address line 1"
        data-testid="summary.billed-to.address.line1"
      />
      <TextFieldInput
        value={localAddress.line2 ?? ''}
        onChange={e => updateField('line2', e.target.value || undefined)}
        placeholder="Address line 2 (optional)"
        data-testid="summary.billed-to.address.line2"
      />
      <TextFieldInput
        value={localAddress.town}
        onChange={e => updateField('town', e.target.value)}
        placeholder="City/town"
        data-testid="summary.billed-to.address.town"
      />
      {stateOptions.length > 0 && (
        <InputSelectControl
          value={localAddress.state ?? ''}
          onChange={newStateValue =>
            updateField('state', newStateValue as Address['state'])
          }
          options={stateOptions}
          placeholder="Choose state..."
          data-testid="summary.billed-to.address.state"
        />
      )}
      <Tooltip
        label="Country cannot be changed while GoCardless is enabled"
        isDisabled={!isCountryLocked}
      >
        <InputSelectControl
          value={localAddress.country}
          onChange={handleCountryChange}
          options={countryOptions}
          placeholder="Select country"
          data-testid="summary.billed-to.address.country"
          filterable
          disabled={isCountryLocked}
        />
      </Tooltip>
      <TextFieldInput
        value={localAddress.postcode}
        onChange={e => updateField('postcode', e.target.value)}
        placeholder="Postal code"
        data-testid="summary.billed-to.address.postcode"
      />
      <Flex gap="8px" justifyContent="flex-end" paddingTop="8px">
        <Button
          variant="secondary"
          onClick={handleCancel}
          data-testid="summary.billed-to.address.cancel"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!savableAddress}
          data-testid="summary.billed-to.address.save"
        >
          Save
        </Button>
      </Flex>
    </Flex>
  )
}

const toSavableAddress = ({
  country,
  ...address
}: DraftAddress): Address | null => (country ? { ...address, country } : null)
